import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import { getPrazoEntregas } from '../../services/prazoEntregaService';

const columns = [
  { Header: 'ID', accessor: 'idPrazoEntrega' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'Status', accessor: 'status' },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Alterado Por', accessor: 'alteradoPor' },
  { Header: 'Cadastrado Em', accessor: 'cadastradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
  { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
];

const pageSizes = [10, 20, 50, 100];

const PrazoEntregaList = () => {
  const [filters, setFilters] = useState({ nome: '', status: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    columns
      .filter(col => !['cadastradoPor', 'alteradoPor', 'cadastradoEm', 'alteradoEm'].includes(col.accessor))
      .map(col => col.accessor)
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await getPrazoEntregas();
        console.log('Response from API:', response); // Debug log
        // Extrair os dados corretamente da resposta da API
        setData(response.data || []);
      } catch (err) {
        console.error('Erro ao carregar prazos de entrega:', err);
        setError('Erro ao carregar dados');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Filtragem
  const filteredData = Array.isArray(data) ? data.filter(item => {
    return (
      (!filters.nome || item.nome.toLowerCase().includes(filters.nome.toLowerCase())) &&
      (!filters.status || item.status.toLowerCase().includes(filters.status.toLowerCase()))
    );
  }) : [];

  // Paginação
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Prazo de Entrega</h2>
            <Link
              to="/prazo-entrega/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Novo Prazo</span>
            </Link>
          </div>
          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />
          <div className="max-w-full mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                name="nome"
                value={filters.nome}
                onChange={handleFilterChange}
                placeholder="Nome do Prazo"
                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 min-w-0"
              />
              <input
                type="text"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                placeholder="Status"
                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 min-w-0"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          <div className="max-w-full">
            
            {loading && (
              <div className="text-center py-4">
                <div className="text-gray-600">Carregando...</div>
              </div>
            )}
            
            {error && (
              <div className="text-center py-4">
                <div className="text-red-600">{error}</div>
              </div>
            )}
            
            {!loading && !error && (
              <Table
                columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                  if (col.accessor === 'nome') {
                    return {
                      ...col,
                      Cell: (value, row) => (
                        <div className="flex items-center gap-2">
                          <span>{value}</span>
                          <Link
                            to={`/prazo-entrega/${row.idPrazoEntrega}/edit`}
                            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                            title="Editar"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        </div>
                      )
                    };
                  }
                  return col;
                })}
                data={paginatedData}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrazoEntregaList;
